import { useMemo } from "react";
import { VStack } from "@chakra-ui/react";
import { Task } from "features/Resources/Task/models";
import { NewHireJourney } from "models/v2/newHireJourney";
import { ALL_STAGES_MINUS_IMMEDIATE_DEPENDENCY } from "features/Stage/constants";
import { getStageStatus } from "features/Stage/helpers";
import { isTaskOpen } from "features/Resources/Task/helpers";
import StageTaskGroupRow from "./StageTaskGroupRow";

interface TasksByStageViewProps {
  tasks: Task[];
  journey: NewHireJourney;
  hideUpcoming?: boolean;
  hideCompleted?: boolean;
  onTaskClick?: (task: Task) => void;
}

export default function TasksByStageView({ tasks, journey, hideUpcoming = false, hideCompleted = false, onTaskClick }: TasksByStageViewProps) {
  // Group tasks by stageKey, only keeping stages that have tasks
  const stageGroups = useMemo(() => {
    const tasksByStage = new Map<string, Task[]>();

    tasks.forEach((task) => {
      const rawKey = task.stageKey || "preboarding";
      // day1 is absorbed into week1 in the view; remap so tasks land in the right group
      const key = rawKey === "day1" ? "week1" : rawKey;
      if (!tasksByStage.has(key)) tasksByStage.set(key, []);
      tasksByStage.get(key)!.push(task);
    });

    // Build ordered list using canonical stage order, include all stages
    return ALL_STAGES_MINUS_IMMEDIATE_DEPENDENCY
      .map((stage) => ({ stageKey: stage.key, stageLabel: stage.label, tasks: tasksByStage.get(stage.key) ?? [] }));
  }, [tasks]);

  return (
    <VStack spacing={2} align="stretch" w="100%">
      {stageGroups.map(({ stageKey, stageLabel, tasks: stageTasks }) => {
        const status = getStageStatus(journey, stageKey);
        if (hideUpcoming && status === "upcoming") return null;
        if (hideCompleted) {
          const openTasks = stageTasks.filter((t) => t.status !== "upcoming" && isTaskOpen(t));
          if (openTasks.length === 0) return null;
        }

        return (
          <StageTaskGroupRow
            key={stageKey}
            stageKey={stageKey}
            stageLabel={stageLabel}
            stageStatus={status}
            tasks={stageTasks}
            hideCompleted={hideCompleted}
            defaultIsOpen
            onTaskClick={onTaskClick}
          />
        );
      })}
    </VStack>
  );
}
